import {
  DestroyRef,
  Directive,
  inject,
  input,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import {
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  skip,
  startWith,
  Subject,
  switchMap,
} from 'rxjs';
import { DocumentType } from 'src/app/_enums/document-type.enum';
import { DocumentUploadModalComponent } from 'src/app/shared/components/document-upload-modal/document-upload-modal.component';

export type SearchStatus = 'idle' | 'loading' | 'success' | 'error';

/**
 * The template every document combobox draws identically; it only refers to
 * names `DocumentComboboxBase` defines.
 */
export const DOCUMENT_COMBOBOX_TEMPLATE = `
  <app-combobox
    [control]="control()"
    [items]="documents()"
    [itemTemplate]="documentTemplate"
    [displayValue]="title"
    [placeholder]="placeholder"
    [disabled]="!searchReady()"
    [loading]="status() === 'loading'"
    [loadingText]="loadingText"
    [emptyText]="status() === 'success' ? emptyText : ''"
    [errorText]="status() === 'error' ? errorText : ''"
    ariaLabel="Document"
    i18n-ariaLabel
    [(searchValue)]="search"
    (inputFocus)="refresh$.next()"
    [compareWith]="sameItem"
    [actionLabel]="canUpload ? uploadLabel : ''"
    (actionSelected)="openUploadModal()"
  />

  <ng-template #documentTemplate let-document>
    <div class="document-option">
      <span class="document-option__title">{{ title(document) }}</span>
      <span class="document-option__details">{{ details(document) }}</span>
    </div>
  </ng-template>

  @if (control().touched && control().hasError('required')) {
    <div class="invalid" i18n>Ce champ est obligatoire.</div>
  }

  @if (canUpload) {
    <app-document-upload-modal
      [userId]="userId()!"
      [memberId]="uploadMemberId()!"
      [type]="uploadType()"
      [hideCustomerFields]="true"
      (documentUploaded)="onUploaded($event)"
    />
  }
`;

export const DOCUMENT_COMBOBOX_STYLES = `
  .document-option {
    display: flex;
    flex-direction: column;
  }

  .document-option__title {
    color: var(--gray-700);
    font-size: 14px;
    font-weight: 500;
  }

  .document-option__details {
    color: var(--gray-500);
    font-size: 12px;
  }
`;

/**
 * What every single-select document combobox has in common: the debounced
 * server-side search with its refetch-on-focus and echo guard, the "none"
 * sentinel mechanics, the control wiring, and the embedded upload modal — on
 * top of the shared template above.
 *
 * A subclass owns its scoping inputs and hands them over through `connect`,
 * called from its constructor with the service call to run; everything else —
 * item type, sentinel, labels, upload prefill — is named by the abstract
 * members. `required` stays declared on each subclass because their defaults
 * differ, surfaced here through `isRequired`.
 */
@Directive()
export abstract class DocumentComboboxBase<T> implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  readonly control = input.required<FormControl<T | null>>();

  /** The account holder; the upload action only shows once known. */
  readonly userId = input<string | null>(null);

  readonly limit = input<number>(20);

  /** What the sentinel entry and the empty field read, e.g. "Aucune". */
  readonly noneLabel = input($localize`Aucun`);

  readonly search = signal('');

  readonly documents = signal<T[]>([]);

  readonly status = signal<SearchStatus>('idle');

  readonly loadingText = $localize`Chargement des documents…`;

  readonly emptyText = $localize`Aucun document trouvé`;

  readonly errorText = $localize`Impossible de charger les documents`;

  protected readonly uploadLabel = $localize`+ Ajouter un document`;

  /** Fired on focus, so every opening of the dropdown shows a fresh list. */
  protected readonly refresh$ = new Subject<void>();

  /**
   * An upload the next search answer should select. Cleared by that answer
   * either way, so a slow upload cannot hijack a pick the agent made since.
   */
  private pendingSelectionId: number | null = null;

  private readonly searchChanges = toObservable(this.search);

  private readonly uploadModal = viewChild(DocumentUploadModalComponent);

  /**
   * Sentinel first entry of an optional picker.
   *
   * app-combobox only writes the control when an option is picked and clears
   * itself only when the selected option is re-picked by identity — which
   * never happens with server-side results. An explicit entry is the one
   * reliable way to say "none"; picking it lands `null` in the control.
   */
  protected abstract readonly sentinel: T;

  /** The subclass's `required` input; declared there, its default differs. */
  protected abstract isRequired(): boolean;

  /** When the input is usable — the search's own scope is resolved. */
  protected abstract searchReady(): boolean;

  /** Whom an upload belongs to; also gates the action row with `userId`. */
  protected abstract uploadMemberId(): string | null;

  /** What the upload form prefills as "Type de document". */
  protected abstract uploadType(): DocumentType;

  /** The store id an upload comes back with (`id` vs `documentId`). */
  protected abstract itemId(item: T): number;

  protected abstract itemTitle(item: T): string;

  protected abstract itemDetails(item: T): string;

  /**
   * Wires the search stream; a subclass calls this from its constructor with
   * its own scope observable and the service call to run. `run` returns null
   * while the scope cannot answer yet, and the picker stays idle.
   */
  protected connect<S>(
    scope$: Observable<S>,
    run: (scope: S, query: string) => Observable<T[]> | null
  ): void {
    combineLatest({
      refresh: this.refresh$.pipe(startWith(undefined)),
      scope: scope$,
      query: this.searchChanges.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        // The text was just echoed from the control, not typed: searching for
        // it would filter every other option out of the dropdown.
        filter((query) => {
          const selected = this.control().value;
          return !selected || query !== this.title(selected);
        }),
        startWith('')
      ),
    })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(({ scope, query }) => {
          const results$ = run(scope, query);
          if (!results$) {
            return of({ status: 'idle' as const, documents: [] as T[] });
          }
          return results$.pipe(
            map((documents) => ({ status: 'success' as const, documents })),
            startWith({ status: 'loading' as const, documents: [] as T[] }),
            catchError(() =>
              of({ status: 'error' as const, documents: [] as T[] })
            )
          );
        })
      )
      .subscribe((state) => {
        this.status.set(state.status);
        // The sentinel heads every answer — loading, empty and error included —
        // so "none" stays pickable whatever the search returned.
        this.documents.set(
          this.isRequired()
            ? state.documents
            : [this.sentinel, ...state.documents]
        );
        if (state.status === 'success' && this.pendingSelectionId !== null) {
          const uploaded = state.documents.find(
            (item) => this.itemId(item) === this.pendingSelectionId
          );
          this.pendingSelectionId = null;
          // Absent when the search does not surface the upload — a filtering
          // query, or a store the search does not read: the pick then stands.
          if (uploaded) {
            this.control().setValue(uploaded);
          }
        }
      });
  }

  /** The fresh list will carry the upload; select it once it does. */
  protected onUploaded(documentId: number): void {
    this.pendingSelectionId = documentId;
    this.refresh$.next();
  }

  ngOnInit(): void {
    const control = this.control();
    control.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((document) => {
        if (document === this.sentinel) {
          control.setValue(null);
          return;
        }
        this.search.set(document ? this.title(document) : '');
      });

    this.searchChanges
      .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((query) => {
        const selected = control.value;
        if (selected && query !== this.title(selected)) {
          control.setValue(null, { emitEvent: false });
        }
      });
  }

  protected get placeholder(): string {
    return this.isRequired() ? '' : this.noneLabel();
  }

  /** The upload form needs the owner: no action row until both ids are in. */
  protected get canUpload(): boolean {
    return !!this.userId() && !!this.uploadMemberId();
  }

  protected openUploadModal(): void {
    this.uploadModal()?.openModal();
  }

  title = (item: T): string =>
    item === this.sentinel ? this.noneLabel() : this.itemTitle(item);

  /**
   * Checkmark pairing for `app-combobox`: refetched lists carry new instances
   * of the same documents, so the selected one is recognized by its id.
   */
  protected readonly sameItem = (option: T, value: T): boolean =>
    option !== this.sentinel && this.itemId(option) === this.itemId(value);

  details(item: T): string {
    return item === this.sentinel ? '' : this.itemDetails(item);
  }
}
